import turfBBox from '@turf/bbox';
import { lineString as turfLineString } from '@turf/helpers';
import { SheetVersion } from 'records/SheetVersion';
import Immutable from 'immutable';
import { SheetEntry } from 'records/AppState';
import { PageId } from 'records/Page';
import { GeoLocation } from 'records/GeoLocation';
import WebMercatorViewport from '@math.gl/web-mercator';
import { Project } from 'records/Project';

const BBHQ = { longitude: -98.4895319, latitude: 39.9571288 };

const defaultViewport = {
  ...BBHQ,
  zoom: 17,
  pitch: 0,
  bearing: 0,
};

const _pdfPointToPixelPoint = (
  pdfPoint: Point,
  sheetVersion: SheetVersion
): Point => {
  const x = pdfPoint.x * sheetVersion.width;
  const y = pdfPoint.y * sheetVersion.height;
  return { x, y };
};

const getCroppedPDFSourceUrl = (
  sheetVersion: SheetVersion,
  cropPath: Point[],
  pageUrl: string,
  canvasId: string
): Promise<string> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.height = sheetVersion.height;
    canvas.width = sheetVersion.width;
    canvas.id = canvasId;
    document.body.style.overflow = 'hidden';
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    const image = new Image();

    ctx.imageSmoothingEnabled = false;

    image.onload = () => {
      ctx.save();

      if (cropPath.length > 0) {
        const { x: x0, y: y0 } = _pdfPointToPixelPoint(
          cropPath[0],
          sheetVersion
        );
        ctx.moveTo(x0, y0);

        cropPath.slice(1).forEach((cropVertex) => {
          const { x, y } = _pdfPointToPixelPoint(cropVertex, sheetVersion);
          ctx.lineTo(x, y);
        });

        ctx.closePath();
      }

      ctx.clip();
      ctx.drawImage(image, 0, 0, canvas.offsetWidth, canvas.offsetHeight);
      ctx.restore();

      resolve(canvas.toDataURL());
      document.getElementById(canvasId)?.remove();
      document.body.style.overflow = 'initial';
    };

    image.crossOrigin = 'anonymous';
    image.src = pageUrl;
  });
};

const bboxToBounds = (bbox: BBox | number[]): Bounds => {
  const [minX, minY, maxX, maxY] = bbox;
  return [
    [minX, minY],
    [maxX, maxY],
  ];
};

const getBoundingBoxOfImage = (coordinates: number[][]): number[] => {
  const line = turfLineString(coordinates);
  return turfBBox(line);
};

const getProjectCenterPointViewport = (location: GeoLocation): Viewport => {
  return {
    ...defaultViewport,
    latitude: location.center[1],
    longitude: location.center[0],
  };
};

const getProjectCenterBounds = (center: [number, number]): Bounds => {
  const [centerX, centerY] = center;
  return [
    [centerX - 0.01, centerY - 0.01],
    [centerX + 0.01, centerY + 0.01],
  ];
};

const calculateAllLayersBoundingBox = (
  currentMappedSheets: Immutable.OrderedMap<PageId, SheetEntry>
): Bounds => {
  let sheetsCoordinates: number[][] = [];

  currentMappedSheets.forEach((sheet) => {
    const sheetCorners =
      sheet?.mappedSheetVersion?.imagesource?.calibrationconfig.corners;

    const coordinates = [
      [sheetCorners?.topleft.longitude, sheetCorners?.topleft.latitude],
      [sheetCorners?.topright.longitude, sheetCorners?.topright.latitude],
      [sheetCorners?.bottomright.longitude, sheetCorners?.bottomright.latitude],
      [sheetCorners?.bottomleft.longitude, sheetCorners?.bottomleft.latitude],
    ];

    sheetsCoordinates = sheetsCoordinates.concat(coordinates as number[][]);
  });

  const calculatedBBox = getBoundingBoxOfImage(sheetsCoordinates);
  const [minX, minY, maxX, maxY] = calculatedBBox;

  return [
    [minX, minY],
    [maxX, maxY],
  ];
};

const getProjectBboxViewport = (
  bbox: Bounds,
  headerHeight: number,
  mapContainer?: string
): Viewport => {
  let options = { padding: { top: 0, left: 60, bottom: 0, right: 60 } };

  if (mapContainer === 'view-map-layers-overlay') {
    const mapLayersOverlay = document.querySelector('#view-map-layers-overlay');
    const mapLayersOverlayWidth = mapLayersOverlay
      ? mapLayersOverlay.clientWidth
      : 0;
    const viewportWidth = window.innerWidth;
    // Check if viewport is wider than the map layers overlay
    // If it's not an @math.gl/web-mercator: assertion failed. error is thrown
    const boundsPaddingLeft =
      viewportWidth > mapLayersOverlayWidth ? mapLayersOverlayWidth : 0;

    options = {
      padding: { top: 0, left: boundsPaddingLeft, bottom: 0, right: 0 },
    };
  }

  const { latitude, longitude, zoom, pitch, bearing } = new WebMercatorViewport(
    {
      width: window.innerWidth,
      height: window.innerHeight - headerHeight,
    }
  ).fitBounds(bbox, options);
  return { ...defaultViewport, latitude, longitude, zoom, pitch, bearing };
};

const getProjectLocationViewport = (
  location: GeoLocation,
  currentMappedSheets: Immutable.OrderedMap<PageId, SheetEntry>,
  headerHeight: number,
  mapContainer?: string
): Viewport => {
  if (currentMappedSheets.size > 0) {
    const calculatedBBox = calculateAllLayersBoundingBox(currentMappedSheets);
    return getProjectBboxViewport(calculatedBBox, headerHeight, mapContainer);
  }
  if (location?.bbox?.length > 0) {
    const locationBBox: Bounds = [
      [location.bbox[0], location.bbox[1]],
      [location.bbox[2], location.bbox[3]],
    ];
    return getProjectBboxViewport(locationBBox, headerHeight, mapContainer);
  }
  return getProjectCenterPointViewport(location);
};

const getDefaultViewport = (headerHeight: number, mapContainer?: string) => {
  // BBOX of USA
  const USABbox: Bounds = [
    [-127.85734009, 17.49454069],
    [-67.7846607, 57.1438188],
  ];
  return getProjectBboxViewport(USABbox, headerHeight, mapContainer);
};

const getProjectViewport = (
  location: GeoLocation,
  currentMappedSheets: Immutable.OrderedMap<PageId, SheetEntry>,
  headerHeight: number,
  mapContainer?: string
): Viewport => {
  return location.center.length === 2 || currentMappedSheets.size > 0
    ? getProjectLocationViewport(
        location,
        currentMappedSheets,
        headerHeight,
        mapContainer
      )
    : getDefaultViewport(headerHeight, mapContainer);
};

const getProjectBounds = (
  location: GeoLocation,
  currentMappedSheets: Immutable.OrderedMap<PageId, SheetEntry>
): Bounds | undefined => {
  if (currentMappedSheets.size > 0) {
    return calculateAllLayersBoundingBox(currentMappedSheets);
  }

  if (location.bbox) {
    return bboxToBounds(location.bbox);
  }

  const [centerX, centerY] = location.center;
  if (centerX !== undefined && centerY !== undefined) {
    return getProjectCenterBounds([centerX, centerY]);
  }
};

const getProjectAddress = (project: Project): string => {
  let projectAddress = '';

  if (project.street) {
    projectAddress += `${project.street}`;
  }

  if (project.city) {
    projectAddress += `, ${project.city}`;
  }

  if (project.state) {
    projectAddress += `, ${project.state}`;
  }

  if (project.zipcode) {
    projectAddress += ` ${project.zipcode}`;
  }

  return projectAddress;
};

const geoLocationToStringAddress = (geoLocation: GeoLocation): string => {
  if (geoLocation.place_name) {
    return geoLocation.place_name;
  }

  return '';
};

export {
  getCroppedPDFSourceUrl,
  getBoundingBoxOfImage,
  getProjectViewport,
  getProjectAddress,
  getProjectBounds,
  geoLocationToStringAddress,
};
